"use client";

// Explainable-wrapped label components for the panchang result sections, split
// out of Panchang.sections.tsx to keep that file under the 500-LOC ceiling (G03).
// Hooks rules: each must be a named component, never an inline render — so they
// live here as exports and are imported by the section components.

import Explainable from "@ishvaram/ui/Explainable";
import { useExplainableHandlers, useExplainer } from "@/hooks/useExplainer";

const NAKSHATRA_HAS_EXPLAINER = new Set([
  "ashwini",
  "bharani",
  "krittika",
  "rohini",
  "mrigashira",
  "ardra",
  "punarvasu",
  "pushya",
  "ashlesha",
  "magha",
  "purva_phalguni",
  "uttara_phalguni",
  "hasta",
  "chitra",
  "swati",
  "vishakha",
  "anuradha",
  "jyeshtha",
  "moola",
  "purva_ashadha",
  "uttara_ashadha",
  "shravana",
  "dhanishta",
  "shatabhisha",
  "purva_bhadrapada",
  "uttara_bhadrapada",
  "revati",
]);

const RASHI_HAS_EXPLAINER = new Set([
  "mesh",
  "vrishabh",
  "mithun",
  "kark",
  "singh",
  "kanya",
  "tula",
  "vrishchik",
  "dhanu",
  "makar",
  "kumbh",
  "meen",
]);

const PLANET_HAS_EXPLAINER = new Set([
  "sun",
  "moon",
  "mars",
  "mercury",
  "jupiter",
  "venus",
  "saturn",
  "rahu",
  "ketu",
]);

export function NakshatraLabel({ nakshatraId, label }: { nakshatraId: string; label?: string }) {
  const hasExplainer = NAKSHATRA_HAS_EXPLAINER.has(nakshatraId);
  const handlers = useExplainableHandlers("panchang_today");
  const { data, isLoading, error, refetch } = useExplainer("nakshatra", nakshatraId, "medium", {
    enabled: false,
  });
  const display = label ?? nakshatraId.replace(/_/g, " ");
  return hasExplainer ? (
    <Explainable
      type="nakshatra"
      id={nakshatraId}
      intensity="medium"
      triggerLabel={display}
      data={data}
      loading={isLoading}
      error={error ?? null}
      onRequestData={() => void refetch()}
      {...handlers}
      className="text-[22px] font-bold capitalize text-ish-dark font-serif"
    />
  ) : (
    <span className="text-[22px] font-bold capitalize text-ish-dark font-serif">{display}</span>
  );
}

export function RashiLabel({
  rashiId,
  className,
  label,
}: {
  rashiId: string;
  className?: string;
  label?: string;
}) {
  const hasExplainer = RASHI_HAS_EXPLAINER.has(rashiId);
  const handlers = useExplainableHandlers("panchang_today");
  const { data, isLoading, error, refetch } = useExplainer("rashi", rashiId, "medium", {
    enabled: false,
  });
  const display = label ?? rashiId.charAt(0).toUpperCase() + rashiId.slice(1);
  return hasExplainer ? (
    <Explainable
      type="rashi"
      id={rashiId}
      intensity="medium"
      triggerLabel={display}
      data={data}
      loading={isLoading}
      error={error ?? null}
      onRequestData={() => void refetch()}
      {...handlers}
      className={className ?? "text-body font-bold capitalize text-ish-dark"}
    />
  ) : (
    <span className={className ?? "text-body font-bold capitalize text-ish-dark"}>{display}</span>
  );
}

export function PlanetLabel({
  planetId,
  symbol,
  labelEn,
  labelHi,
}: {
  planetId: string;
  symbol: string;
  labelEn: string;
  labelHi: string;
}) {
  const hasExplainer = PLANET_HAS_EXPLAINER.has(planetId);
  const handlers = useExplainableHandlers("panchang_today");
  const { data, isLoading, error, refetch } = useExplainer("planet", planetId, "medium", {
    enabled: false,
  });
  return (
    <div className="flex items-center gap-3">
      <span className="text-lg w-7 text-center text-intent-brand">{symbol}</span>
      <div>
        {hasExplainer ? (
          <Explainable
            type="planet"
            id={planetId}
            intensity="medium"
            triggerLabel={labelEn}
            data={data}
            loading={isLoading}
            error={error ?? null}
            onRequestData={() => void refetch()}
            {...handlers}
            className="text-body font-semibold text-ish-dark"
          />
        ) : (
          <span className="text-body font-semibold text-ish-dark">{labelEn}</span>
        )}
        <span className="text-caption ml-1.5 text-ish-faint">{labelHi}</span>
      </div>
    </div>
  );
}

export function RashiPositionLabel({ rashiId, label }: { rashiId: string; label?: string }) {
  const hasExplainer = RASHI_HAS_EXPLAINER.has(rashiId);
  const handlers = useExplainableHandlers("panchang_today");
  const { data, isLoading, error, refetch } = useExplainer("rashi", rashiId, "medium", {
    enabled: false,
  });
  const display = label ?? rashiId.charAt(0).toUpperCase() + rashiId.slice(1);
  return hasExplainer ? (
    <Explainable
      type="rashi"
      id={rashiId}
      intensity="medium"
      triggerLabel={display}
      data={data}
      loading={isLoading}
      error={error ?? null}
      onRequestData={() => void refetch()}
      {...handlers}
      className="text-body font-semibold capitalize text-ish-dark"
    />
  ) : (
    <span className="text-body font-semibold capitalize text-ish-dark">{display}</span>
  );
}
